import { useMemo } from "react";
import { Combobox } from "@/components/ui/combobox";

/**
 * @typedef {Object} VendorLocationFiltersProps
 * @property {Array} vendorItems - Array of items to extract vendors from
 * @property {Array} [locationItems] - Optional array of items to extract locations from (defaults to vendorItems)
 * @property {Function} getVendor - Function to extract vendor from an item
 * @property {Function} getLocation - Function to extract location from an item
 * @property {string[]} selectedVendors - Currently selected vendors
 * @property {string[]} selectedLocations - Currently selected locations
 * @property {Function} onVendorsChange - Callback when vendors change
 * @property {Function} onLocationsChange - Callback when locations change
 * @property {boolean} [disabled] - Whether the filters are disabled
 */

/**
 * Reusable vendor and location filter comboboxes with dynamic location filtering
 * Locations are filtered based on selected vendors
 * @param {VendorLocationFiltersProps} props - Component props
 * @returns {JSX.Element} Vendor and location filter comboboxes
 */
export default function VendorLocationFilters({
  vendorItems,
  locationItems,
  getVendor,
  getLocation,
  selectedVendors,
  selectedLocations,
  onVendorsChange,
  onLocationsChange,
  disabled = false,
}) {
  // Use vendorItems for locations if locationItems not provided
  const itemsForLocations = locationItems || vendorItems;

  // Normalize selected values (trim) so they match option values
  const normalizedSelectedVendors = useMemo(
    () =>
      (selectedVendors || [])
        .map((v) => (typeof v === "string" ? v.trim() : v))
        .filter(Boolean),
    [selectedVendors]
  );
  const normalizedSelectedLocations = useMemo(
    () =>
      (selectedLocations || [])
        .map((l) => (typeof l === "string" ? l.trim() : l))
        .filter(Boolean),
    [selectedLocations]
  );

  // Extract unique vendors from vendor items (trimmed). Include any currently selected
  // so the dropdown shows both available and selected (selected can be deselected).
  const vendorOptions = useMemo(() => {
    const fromItems = (vendorItems || [])
      .map((item) => getVendor(item))
      .filter((v) => v && String(v).trim() !== "")
      .map((v) => String(v).trim());
    const uniqueFromItems = [...new Set(fromItems)].sort((a, b) =>
      a.localeCompare(b)
    );
    const optionValues = new Set(uniqueFromItems);
    const extraSelected = normalizedSelectedVendors.filter(
      (v) => !optionValues.has(v)
    );
    return [
      ...uniqueFromItems.map((vendor) => ({ value: vendor, label: vendor })),
      ...extraSelected.map((vendor) => ({ value: vendor, label: vendor })),
    ];
  }, [vendorItems, getVendor, normalizedSelectedVendors]);

  // Location options always depend on selected vendors: use the full list (vendorItems) and filter by
  // selected vendors in the component so the dropdown updates immediately when the user changes vendor.
  const locationOptions = useMemo(() => {
    const selectedSet = new Set(normalizedSelectedVendors);
    const sourceList = vendorItems || itemsForLocations || [];
    let itemsForLocationFiltering =
      selectedSet.size > 0
        ? sourceList.filter((item) => {
            const v = getVendor(item);
            return v != null && selectedSet.has(String(v).trim());
          })
        : sourceList;

    const fromItems = itemsForLocationFiltering
      .map((item) => getLocation(item))
      .filter((l) => l != null && String(l).trim() !== "")
      .map((l) => String(l).trim());
    const uniqueFromItems = [...new Set(fromItems)].sort((a, b) =>
      a.localeCompare(b)
    );
    const optionValues = new Set(uniqueFromItems);
    const extraSelected = normalizedSelectedLocations.filter(
      (l) => !optionValues.has(l)
    );
    return [
      ...uniqueFromItems.map((location) => ({
        value: location,
        label: location,
      })),
      ...extraSelected.map((location) => ({
        value: location,
        label: location,
      })),
    ];
  }, [
    itemsForLocations,
    vendorItems,
    normalizedSelectedVendors,
    normalizedSelectedLocations,
    getVendor,
    getLocation,
  ]);

  return (
    <>
      <div className='flex flex-col space-y-2'>
        <label className='text-sm font-medium text-foreground'>Vendor:</label>
        <Combobox
          value={normalizedSelectedVendors}
          onValueChange={onVendorsChange}
          options={vendorOptions}
          placeholder='Select vendors...'
          searchPlaceholder='Search vendors...'
          emptyText='No vendors found.'
          multiple={true}
          disabled={disabled}
        />
      </div>

      <div className='flex flex-col space-y-2'>
        <label className='text-sm font-medium text-foreground'>Location:</label>
        <Combobox
          value={normalizedSelectedLocations}
          onValueChange={onLocationsChange}
          options={locationOptions}
          placeholder='Select locations...'
          searchPlaceholder='Search locations...'
          emptyText='No locations found.'
          multiple={true}
          disabled={disabled}
        />
      </div>
    </>
  );
}
